// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — shared colour standards from the stream index (schema 1.4).
 *
 * Datasets that measure the same thing carry one default palette and one
 * pair of colour limits, so two temperature maps use the same colours and
 * can be compared at a glance (owner, 2026-10-09). The publisher declares
 * them once, in `index.json`:
 *
 *   "displayGroups": { "temperature": { "label": "Temperature", "units": "°C",
 *     "palette": "turbo", "inverted": false, "scale": "linear", "min": -25, "max": 50 } }
 *
 * and tags each member row with `"displayGroup": "temperature"`. The
 * publisher validates both; this checks again and drops anything it can't
 * use, because a dataset must never fail to show because of its group: it
 * then simply keeps its own published colours. See
 * docs/AYNI_XR_PALETTE_GROUPS_PLAN.md.
 */

import { PALETTE_IDS, type ColorScaleDisplay, type PaletteId } from './colorScaleDisplay'

/** One colour standard, as this app uses it. */
export interface DisplayGroup {
  /** The group's id (`temperature`): stable, never reused for another meaning. */
  id: string
  /** English, for logs only; the interface names groups in its own words. */
  label: string
  units: string
  palette: PaletteId
  inverted: boolean
  /** The colour axis the limits are spread on, whatever the stream's own encoding. */
  scale: 'linear' | 'log'
  /** Physical values in `units`: the palette's first and last colours. */
  min: number
  max: number
}

const GROUP_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/** One group from the index, or null when any field is unusable here. */
function parseGroup(id: string, raw: unknown): DisplayGroup | null {
  if (!GROUP_ID.test(id) || !raw || typeof raw !== 'object') return null
  const g = raw as Record<string, unknown>
  const palette = g.palette
  if (typeof palette !== 'string' || !(PALETTE_IDS as readonly string[]).includes(palette) || palette === 'source') return null
  if (g.scale !== 'linear' && g.scale !== 'log') return null
  if (typeof g.inverted !== 'boolean' || typeof g.units !== 'string') return null
  const min = g.min
  const max = g.max
  if (typeof min !== 'number' || typeof max !== 'number' || !Number.isFinite(min) || !Number.isFinite(max) || !(min < max)) return null
  if (g.scale === 'log' && !(min > 0)) return null
  return {
    id,
    label: typeof g.label === 'string' ? g.label : id,
    units: g.units,
    palette: palette as PaletteId,
    inverted: g.inverted,
    scale: g.scale,
    min,
    max,
  }
}

/** The index's `displayGroups`, keeping only the groups this app can draw. */
export function parseDisplayGroups(raw: unknown): ReadonlyMap<string, DisplayGroup> {
  const out = new Map<string, DisplayGroup>()
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    const group = parseGroup(id, value)
    if (group) out.set(id, group)
  }
  return out
}

/** What a row needs for its group to apply. */
export interface GroupedRow {
  displayGroup?: unknown
  units?: string
  valueEncoded?: boolean
}

/**
 * A row's group, or undefined: the row names a known group, carries
 * values, and is in the group's units. Anything else keeps the row's own
 * colours, silently.
 */
export function groupOfRow(groups: ReadonlyMap<string, DisplayGroup>, row: GroupedRow): DisplayGroup | undefined {
  if (typeof row.displayGroup !== 'string' || row.valueEncoded !== true) return undefined
  const group = groups.get(row.displayGroup)
  if (!group || (row.units ?? '') !== group.units) return undefined
  return group
}

/** The display a group opens a dataset with: its palette and inversion, its limits on its own axis. */
export function groupDisplay(group: DisplayGroup): ColorScaleDisplay {
  return {
    palette: group.palette,
    inverted: group.inverted,
    stretch: { lo: 0, hi: 1 },
    limits: { min: group.min, max: group.max, scale: group.scale },
    threshold: { min: null, max: null },
  }
}
